import type { PayloadAction } from '@reduxjs/toolkit';
import type { WebsocketRole, WebsocketState } from './types';

import { createSlice } from '@reduxjs/toolkit';

const initialState: WebsocketState = {
  connected: false,
  socketId: null,
  rooms: [],
  error: null,
};

const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    // Connection actions
    connectRequest: (state) => {
      state.error = null;
    },
    connectSuccess: (state, action: PayloadAction<{ socketId: string }>) => {
      state.connected = true;
      state.socketId = action.payload.socketId;
      state.error = null;
    },
    connectFailure: (state, action: PayloadAction<{ error: string }>) => {
      state.connected = false;
      state.socketId = null;
      state.error = action.payload.error;
    },
    disconnect: (state) => {
      state.connected = false;
      state.socketId = null;
      // Keep rooms in state so they can be rejoined on reconnect
    },

    // Room join actions
    joinRequest: (
      state,
      action: PayloadAction<{ domain: string; room: string; access: WebsocketRole }>
    ) => {
      state.error = null;
    },
    joinSuccess: (
      state,
      action: PayloadAction<{ domain: string; room: string; access: WebsocketRole }>
    ) => {
      const { domain, room, access } = action.payload;
      // Check if room is already joined
      const existingRoom = state.rooms.find((r) => r.domain === domain && r.room === room);
      if (!existingRoom) {
        state.rooms.push({ domain, room, access });
      }
      state.error = null;
    },
    joinFailure: (
      state,
      action: PayloadAction<{ domain: string; room: string; access: WebsocketRole; error: string }>
    ) => {
      state.error = action.payload.error;
    },

    // Room leave actions
    leaveRequest: (
      state,
      action: PayloadAction<{ domain: string; room: string; access: WebsocketRole }>
    ) => {
      state.error = null;
    },
    leaveSuccess: (
      state,
      action: PayloadAction<{ domain: string; room: string; access: WebsocketRole }>
    ) => {
      const { domain, room } = action.payload;
      state.rooms = state.rooms.filter((r) => r.domain !== domain || r.room !== room);
      state.error = null;
    },
    leaveFailure: (
      state,
      action: PayloadAction<{ domain: string; room: string; access: WebsocketRole; error: string }>
    ) => {
      state.error = action.payload.error;
    },

    // Error handling
    setError: (state, action: PayloadAction<{ error: string }>) => {
      state.error = action.payload.error;
    },
    clearError: (state) => {
      state.error = null;
    },

    // Clear rooms (used on logout)
    clearRooms: (state) => {
      state.rooms = [];
    },
  },
});

export const {
  connectRequest,
  connectSuccess,
  connectFailure,
  disconnect,
  joinRequest,
  joinSuccess,
  joinFailure,
  leaveRequest,
  leaveSuccess,
  leaveFailure,
  setError,
  clearError,
  clearRooms,
} = websocketSlice.actions;

export default websocketSlice.reducer;
